import { useCallback, useState, useRef } from 'react';
import axios from 'axios';

export const useFieldValidation = () => {
    const [validationStates, setValidationStates] = useState({});
    const timeoutRefs = useRef({});

    const validateField = useCallback(async (fieldName, value, endpoint) => {
        // Limpiar timeout anterior si existe
        if (timeoutRefs.current[fieldName]) {
            clearTimeout(timeoutRefs.current[fieldName]);
        }

        const trimmed = (value ?? '').trim();

        // Reglas de validación en cliente
        if (trimmed === '') {
            if (fieldName === 'email') {
                // Email es obligatorio
                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: 'invalid',
                        message: 'El correo es obligatorio',
                        isValid: false,
                    },
                }));
            } else {
                // Teléfono opcional u otros
                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: 'idle',
                        message: '',
                        isValid: true,
                    },
                }));
            }
            return;
        }

        // Validación de formato
        if (fieldName === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
            if (!emailRegex.test(trimmed)) {
                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: 'invalid',
                        message: 'Formato de correo no válido',
                        isValid: false,
                    },
                }));
                return;
            }
        }

        if (fieldName === 'phone') {
            // Solo números, máximo 15 dígitos
            const phoneRegex = /^\d{1,15}$/;
            if (!phoneRegex.test(trimmed)) {
                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: 'invalid',
                        message: 'Solo números (máx. 15 dígitos)',
                        isValid: false,
                    },
                }));
                return;
            }
        }

        // Establecer estado de validando antes de la consulta remota
        setValidationStates(prev => ({
            ...prev,
            [fieldName]: {
                status: 'validating',
                message: 'Verificando disponibilidad...',
                isValid: false,
            },
        }));

        // Configurar debounce de 800ms
        timeoutRefs.current[fieldName] = setTimeout(async () => {
            try {
                const response = await axios.post(endpoint, {
                    [fieldName]: trimmed,
                });

                const { available, message } = response.data;

                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: available ? 'valid' : 'invalid',
                        message,
                        isValid: available,
                    },
                }));
            } catch (error) {
                console.error(`Error validating ${fieldName}:`, error);

                let errorMessage = 'Error al verificar disponibilidad';
                if (error.response?.data?.errors?.[fieldName]) {
                    errorMessage = error.response.data.errors[fieldName][0];
                }

                setValidationStates(prev => ({
                    ...prev,
                    [fieldName]: {
                        status: 'error',
                        message: errorMessage,
                        isValid: false,
                    },
                }));
            }
        }, 800);
    }, []);

    const validateEmail = useCallback((email) => {
        validateField('email', email, '/validate/email');
    }, [validateField]);

    const validatePhone = useCallback((phone) => {
        validateField('phone', phone, '/validate/phone');
    }, [validateField]);

    const getFieldState = useCallback((fieldName) => {
        return validationStates[fieldName] || {
            status: 'idle',
            message: '',
            isValid: fieldName === 'email' ? false : true, // Email requerido por defecto inválido hasta validar
        };
    }, [validationStates]);

    const isFormValid = useCallback(() => {
        return Object.values(validationStates).every((state) => state.isValid);
    }, [validationStates]);

    // Limpiar timeouts al desmontar
    const cleanup = useCallback(() => {
        Object.values(timeoutRefs.current).forEach((timeout) => {
            if (timeout) clearTimeout(timeout);
        });
        timeoutRefs.current = {};
    }, []);

    return {
        validateEmail,
        validatePhone,
        getFieldState,
        isFormValid,
        cleanup,
    };
};
